import React, { useMemo, useCallback } from "react";
import {
  User,
  TriangleDashed,
  UserRoundPlus,
  CircleDashed,
  Square,
  ClockFading,
  Search,
} from "lucide-react";
import { STATUS_LIST } from "../../ProjectList/CanvasKanban";
import { defaultPriorities, NoPriorityIcon } from "@/types/priorities";
import { UserAvatar } from "../../common/UserAvatar";
import HalfFilledTriangleIcon from "@/assets/icons/half-filled-triangle-icon";
import type { ProjectUser, ProjectLabel } from "../../ProjectList/types";
import { FilterType, DueDateFilterValue } from "@/types/filterEnums";

type FilterValue = string | number;

interface UseFilterIconsProps {
  userLookupMap: Map<string, ProjectUser>;
  labelLookupMap: Map<string, ProjectLabel>;
}

export const useFilterIcons = ({
  userLookupMap,
  labelLookupMap,
}: UseFilterIconsProps) => {
  const filterIconMap = useMemo(() => {
    const iconProps = { className: "w-3 h-3", color: "var(--secondary-n-600)" };
    const squareProps = {
      fill: "var(--secondary-n-600)",
      color: "var(--secondary-n-600)",
      className: "w-3 h-3",
    };

    return {
      [FilterType.SEARCH]: <Search {...iconProps} />,
      [FilterType.ASSIGNEE]: <UserRoundPlus {...iconProps} />,
      [FilterType.STATUS]: <CircleDashed {...iconProps} />,
      [FilterType.PRIORITY]: <NoPriorityIcon {...iconProps} />,
      [FilterType.ESTIMATE]: <TriangleDashed {...iconProps} />,
      [FilterType.LABEL]: <Square {...squareProps} />,
      [FilterType.DUE_DATE]: <ClockFading {...iconProps} />,
    } as const;
  }, []);

  const statusLookupMap = useMemo(() => {
    return new Map(STATUS_LIST.map((status) => [status.id, status]));
  }, []);

  const priorityLookupMap = useMemo(() => {
    return new Map(
      defaultPriorities.map((priority) => [priority.id, priority]),
    );
  }, []);

  const fallbackIcons = useMemo(
    () => ({
      user: <User className="w-3 h-3" color="var(--secondary-n-600)" />,
      status: (
        <CircleDashed className="w-3 h-3" color="var(--secondary-n-600)" />
      ),
      priority: (
        <NoPriorityIcon className="w-3 h-3" color="var(--secondary-n-600)" />
      ),
      estimate: (
        <TriangleDashed className="w-3 h-3" color="var(--secondary-n-600)" />
      ),
      label: (
        <Square
          fill="var(--secondary-n-600)"
          color="var(--secondary-n-600)"
          className="w-3 h-3"
        />
      ),
      dueDate: (
        <ClockFading className="w-3 h-3" color="var(--secondary-n-600)" />
      ),
    }),
    [],
  );

  const getFilterIcon = useCallback(
    (filterType: string): React.ReactNode => {
      return filterIconMap[filterType as keyof typeof filterIconMap] || null;
    },
    [filterIconMap],
  );

  const getValueIcon = useCallback(
    (filterType: string, value: FilterValue): React.ReactNode => {
      if (value === DueDateFilterValue.NONE) return null;

      switch (filterType) {
        case FilterType.ASSIGNEE: {
          const user = userLookupMap.get(String(value));
          return user ? (
            <UserAvatar
              userName={user.name}
              userId={user.id}
              className="w-3 h-3"
            />
          ) : (
            fallbackIcons.user
          );
        }
        case FilterType.STATUS: {
          const status = statusLookupMap.get(String(value));
          return status?.icon ? <status.icon /> : fallbackIcons.status;
        }
        case FilterType.PRIORITY: {
          const priorityValue =
            typeof value === "string" ? parseInt(value) : value;
          const priority = priorityLookupMap.get(priorityValue);
          return priority ? (
            <priority.icon className="w-3 h-3" />
          ) : (
            fallbackIcons.priority
          );
        }
        case FilterType.ESTIMATE: {
          return value ? (
            <HalfFilledTriangleIcon className="w-3 h-3" />
          ) : (
            fallbackIcons.estimate
          );
        }
        case FilterType.LABEL: {
          const label = labelLookupMap.get(String(value));
          if (!label) return fallbackIcons.label;

          const backgroundColor =
            (typeof label.color === "object" && label.color?.value) ||
            (typeof label.color === "string" ? label.color : null) ||
            "var(--secondary-n-600)";

          return (
            <div
              className="w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor }}
            />
          );
        }
        case FilterType.DUE_DATE: {
          return fallbackIcons.dueDate;
        }
        default: {
          throw new Error(
            `Unhandled filter type: ${filterType}. Please add a case for this filter type.`,
          );
        }
      }
    },
    [
      userLookupMap,
      labelLookupMap,
      statusLookupMap,
      priorityLookupMap,
      fallbackIcons,
    ],
  );

  const getValueIcons = useCallback(
    (filterType: string, values: FilterValue[]): React.ReactNode[] => {
      if (!values.length) return [];

      const icons: React.ReactNode[] = [];
      for (let i = 0; i < values.length; i++) {
        const icon = getValueIcon(filterType, values[i]);
        if (icon) icons.push(icon);
      }
      return icons;
    },
    [getValueIcon],
  );

  return {
    getFilterIcon,
    getValueIcon,
    getValueIcons,
  };
};
